<template>
  <div v-if="hasAny" class="ai-assistant-topic-fence" :class="{ 'ai-assistant-topic-fence--panel-compact': isPanelCompactLayout }">
    <div
      v-if="topicTree.length"
      class="ai-assistant-topic-fence__scroll"
      :class="{
        'ai-assistant-topic-fence__scroll--scaled': topicNeedsScale,
        'ai-assistant-topic-fence__scroll--compact': isTopicLayoutCompact && !isPanelCompactLayout
      }"
      ref="topicScrollEl"
    >
      <div
        class="ai-assistant-topic-fence__scale-shim"
        :class="{ 'ai-assistant-topic-fence__scale-shim--panel-compact': isPanelCompactLayout }"
        :style="topicScaleShimStyle"
      >
        <div
          class="ai-assistant-topic-fence__scale-inner"
          :class="{ 'ai-assistant-topic-fence__scale-inner--panel-compact': isPanelCompactLayout }"
          :style="topicScaleInnerStyle"
        >
          <div
            class="topicBox"
            ref="topicBoxEl"
            :class="{ 'topicBox--compact': isTopicLayoutCompact, 'topicBox--panel-compact': isPanelCompactLayout }"
            :style="panelCompactTopicStyle"
          >
        <div v-for="(item, index) in normalizedTopicTree" :key="'t-' + instanceUid + '-' + index" class="topicItem">
          <div class="topicName">
            <img
              class="topicNameImg"
              :src="'https://img.webloom.cn/img/aiChooseBg' + (index + 1) + '.png'"
              alt=""
            />
            <div class="topicNameTxt">{{ item.label }}</div>
          </div>
          <aiNameBigTree
            :width="aiNameBigWidth"
            :key="'big-' + instanceUid + '-' + index + '-' + item.labelId"
            :filter-uid="'fbt-' + instanceUid + '-' + index"
            :fillColor1="topicColor[index * 3 + 1]"
            :fillColor2="topicColor[index * 3 + 2]"
            :fillColor3="topicColor[index * 3 + 0]"
          />
          <div class="topicDescBox">
            <div
              class="topicDesc"
              :class="'topicDesc' + (descIndex + 1)"
              v-for="(desc, descIndex) in item.value"
              :key="'d-' + instanceUid + '-' + desc.labelId"
            >
              <div class="topicDescItem">
                <aiNameBackground :width="aiNameWidth" :fillColor="topicColor[index * 3 + descIndex]" />
                <div
                  :id="descLabelId(index, descIndex)"
                  class="topicDescLabel"
                >{{ desc.label }}</div>
                <aiChooseBorder
                  :width="aiSecondWidth"
                  :height="secondDescHeight(index, descIndex)"
                  class="topicDescBorder"
                  fillColor="#ffffff"
                  v-if="topicRelationHas(desc.labelId)"
                />
              </div>
              <aiNameTree
                :width="aiNameTreeWidth"
                class="topicDescTree"
                :fillColor="topicColor[index * 3 + descIndex]"
                :strokeColor="topicColor[index * 3 + descIndex]"
              />
              <div class="topicDescItemBox">
                <div
                  class="topicDescItemB"
                  :class="'topicDescItemB' + (itemIndex + 1)"
                  v-for="(value, itemIndex) in desc.value"
                  :key="'v-' + instanceUid + '-' + value.labelId"
                >
                  <div
                    :style="{ color: fontColor[index * 3 + descIndex] }"
                    :id="descItemTxtId(index, descIndex, itemIndex)"
                    class="topicDescItemTxt"
                  >{{ value.label }}</div>
                  <aiNameColor
                    class="topicDescItemColor"
                    :filter-uid="'fc-' + instanceUid + '-' + index + '-' + descIndex + '-' + itemIndex"
                    :width="colorBarWidth(index, descIndex, itemIndex)"
                    :fillColor="topicColor[index * 3 + descIndex]"
                  />
                  <aiChooseBorder
                    class="topicDescItemBorder"
                    :width="colorBarWidth(index, descIndex, itemIndex) + 15"
                    :fillColor="topicColor[index * 3 + descIndex]"
                    v-if="topicRelationHas(value.labelId)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="topicList.length" class="topicTextBox">
      <div class="topicTitleBox">
        <div class="topicTitleBoxItem">
          <div class="topicTitle">{{ currentTopic.topicName }}</div>
          <img
            @click="clickTopicTitle"
            class="topicTitleImg"
            src="https://img.webloom.cn/img/aiChooseRefuleDefault.png"
            alt=""
          />
        </div>
      </div>
      <div class="topicDescTxt">{{ currentTopic.topicDesc }}</div>
    </div>
  </div>
</template>

<script>
import aiNameTree from '../ai-name-tree/ai-name-tree.vue'
import aiNameColor from '../ai-name-color/ai-name-color.vue'
import aiNameBigTree from '../ai-name-big-tree/ai-name-big-tree.vue'
import aiNameBackground from '../ai-name-background/ai-name-background.vue'
import aiChooseBorder from '../ai-choose-border1/ai-choose-border1.vue'

/** 放大模式下视口低于此宽度时，选题树按缩小模式居中竖排 */
const FULL_SCREEN_TOPIC_COMPACT_MAX_WIDTH = 1200

/**
 * 伴学助手 IM fence：```topicTree``` + ```topicNameListStr``` 展示（与首页 AboutBloom 选题区视觉一致，独立实例不污染首页）
 */
export default {
  name: 'AiAssistantTopicFence',
  components: {
    aiNameTree,
    aiNameColor,
    aiNameBigTree,
    aiNameBackground,
    aiChooseBorder
  },
  props: {
    topicTree: {
      type: Array,
      default () {
        return []
      }
    },
    topicList: {
      type: Array,
      default () {
        return []
      }
    },
    /** 伴学助手缩小模式：强制单列排布 + 固定最小字号 */
    panelCompact: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      instanceUid: '',
      showAiTopic: 0,
      aiNameBigWidth: 96,
      aiNameWidth: 104,
      aiNameTreeWidth: 43,
      aiSecondWidth: 70,
      aiNameColorWidth: [],
      aiSecondHeight: [],
      topicRelationNodeIds: [],
      /** 聊天列较窄：三棵 topicItem 竖向堆叠（与「是否缩放」独立） */
      topicLayoutCompact: false,
      /** 聊天列装不下设计宽度时：整体 scale（仍可保持横向三列） */
      topicNeedsScale: false,
      /** 选题大图按聊天列宽等比缩放，视觉宽度不超过父级 */
      topicBoxScale: 1,
      topicShimHeight: 0,
      /** 伴学缩小模式：JS/CSS 共用同一缩放系数，避免 SVG 与文字框错位 */
      layoutScale: 1,
      /** document.documentElement.clientWidth，用于放大模式窄视口切换树形布局 */
      documentViewportWidth: 1920,
      topicColor: [
        '#F6A04D',
        '#9E82D9',
        '#4B92E4',
        '#4D7DE9',
        '#F6A04D',
        '#9E82D9',
        '#DA7A84',
        '#296BFF',
        '#6B81FA'
      ],
      fontColor: [
        '#613204',
        '#320F7B',
        '#2C588B',
        '#162A57',
        '#613204',
        '#44297E',
        '#DA7A84',
        '#296BFF',
        '#6B81FA'
      ]
    }
  },
  computed: {
    /** 与接口字段对齐：支持 value / children / childList / nodes */
    normalizedTopicTree () {
      const raw = this.topicTree
      if (!Array.isArray(raw) || !raw.length) return []
      return raw.map((item, index) => this.normalizeTopicRoot(item, index))
    },
    hasAny () {
      return (this.topicTree && this.topicTree.length > 0) || (this.topicList && this.topicList.length > 0)
    },
    /** 缩小模式，或放大模式下视口 < 1200px：单列居中树形布局 */
    isPanelCompactLayout () {
      if (this.panelCompact) return true
      const vw = this.documentViewportWidth
      return vw > 0 && vw < FULL_SCREEN_TOPIC_COMPACT_MAX_WIDTH
    },
    /** 竖排：伴学缩小模式强制单列，或聊天列过窄时自动竖排 */
    isTopicLayoutCompact () {
      return this.isPanelCompactLayout || this.topicLayoutCompact
    },
    panelCompactTopicStyle () {
      if (!this.isPanelCompactLayout) return null
      return { '--topic-k': String(this.layoutScale) }
    },
    currentTopic () {
      const list = this.topicList || []
      if (!list.length) return { topicName: '', topicDesc: '' }
      const i = Math.min(Math.max(0, this.showAiTopic), list.length - 1)
      return list[i] || { topicName: '', topicDesc: '' }
    },
    topicScaleInnerStyle () {
      const origin = this.isPanelCompactLayout ? 'top center' : 'top left'
      if (!this.topicNeedsScale) {
        return {
          transform: 'none',
          transformOrigin: origin
        }
      }
      return {
        transform: `scale(${this.topicBoxScale})`,
        transformOrigin: origin
      }
    },
    topicScaleShimStyle () {
      const h = this.topicShimHeight
      const base = {
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }
      if (this.isPanelCompactLayout && this.topicNeedsScale) {
        return {
          ...base,
          height: h > 0 ? `${h}px` : 'auto',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center'
        }
      }
      if (this.isPanelCompactLayout) {
        return {
          ...base,
          height: 'auto',
          overflow: 'visible',
          display: 'flex',
          justifyContent: 'center'
        }
      }
      if (!this.topicNeedsScale) {
        return {
          ...base,
          height: 'auto',
          overflow: 'visible'
        }
      }
      return {
        ...base,
        height: h > 0 ? `${h}px` : 'auto',
        overflow: 'hidden'
      }
    },
  },
  created () {
    this.instanceUid = `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
  },
  mounted () {
    window.addEventListener('resize', this.resize)
    this.resize()
    if (this.topicList && this.topicList.length) {
      this.showAiTopic = Math.min(2, this.topicList.length - 1)
    }
    this.$nextTick(() => {
      this.syncRelationIds()
      this.scheduleMeasure()
      this.ensureTopicBoxFitObserver()
      this.updateTopicFitToChat()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resize)
    this.teardownTopicBoxFitObserver()
  },
  watch: {
    topicTree: {
      deep: true,
      handler () {
        this.$nextTick(() => {
          this.resize()
          this.scheduleMeasure()
          this.ensureTopicBoxFitObserver()
          this.updateTopicFitToChat()
        })
      }
    },
    topicList: {
      deep: true,
      handler (v) {
        if (v && v.length) {
          this.showAiTopic = Math.min(2, v.length - 1)
          this.$nextTick(() => {
            this.syncRelationIds()
          })
        }
      }
    },
    panelCompact () {
      this.resize()
      this.$nextTick(() => {
        this.scheduleMeasure()
        this.updateTopicFitToChat()
      })
    },
    isPanelCompactLayout () {
      this.resize()
      this.$nextTick(() => {
        this.scheduleMeasure()
        this.updateTopicFitToChat()
      })
    }
  },
  methods: {
    topicChildArray (node) {
      if (!node || typeof node !== 'object') return []
      const raw =
        node.value != null
          ? node.value
          : node.children != null
            ? node.children
            : node.childList != null
              ? node.childList
              : node.nodes != null
                ? node.nodes
                : []
      return Array.isArray(raw) ? raw : []
    },
    normalizeTopicRoot (item, index) {
      if (!item || typeof item !== 'object') {
        return { label: '', labelId: `root-${index}`, value: [] }
      }
      const label = item.label != null ? String(item.label) : ''
      const labelId = item.labelId != null ? item.labelId : `root-${index}`
      const level2 = this.topicChildArray(item)
      return {
        label,
        labelId,
        value: level2.map((desc, j) => this.normalizeTopicDesc(desc, index, j))
      }
    },
    normalizeTopicDesc (desc, i, j) {
      if (!desc || typeof desc !== 'object') {
        return { label: '', labelId: `d-${i}-${j}`, value: [] }
      }
      const label = desc.label != null ? String(desc.label) : ''
      const labelId = desc.labelId != null ? desc.labelId : `d-${i}-${j}`
      const leaves = this.topicChildArray(desc)
      return {
        label,
        labelId,
        value: leaves.map((v, k) => this.normalizeTopicLeaf(v, i, j, k))
      }
    },
    normalizeTopicLeaf (v, i, j, k) {
      if (v == null) return { label: '', labelId: `l-${i}-${j}-${k}` }
      if (typeof v !== 'object') {
        return { label: String(v), labelId: `l-${i}-${j}-${k}` }
      }
      return {
        label: v.label != null ? String(v.label) : '',
        labelId: v.labelId != null ? v.labelId : `l-${i}-${j}-${k}`
      }
    },
    colorBarWidth (index, descIndex, itemIndex) {
      const w = this.aiNameColorWidth[(index * 3 + descIndex) * 2 + itemIndex]
      const fallback = this.isPanelCompactLayout ? 48 * this.layoutScale : 48
      return typeof w === 'number' && w > 0 ? w : fallback
    },
    secondDescHeight (index, descIndex) {
      const h = this.aiSecondHeight[index * 3 + descIndex]
      const fallback = this.isPanelCompactLayout ? 28 * this.layoutScale : 28
      return typeof h === 'number' && h > 0 ? h : fallback
    },
    topicRelationHas (labelId) {
      const s = String(labelId)
      return (this.topicRelationNodeIds || []).some(x => String(x) === s)
    },
    descLabelId (index, descIndex) {
      return `aas-tdl-${this.instanceUid}-${index * 3 + descIndex + 1}`
    },
    descItemTxtId (index, descIndex, itemIndex) {
      return `aas-tdit-${this.instanceUid}-${(index * 3 + descIndex) * 2 + itemIndex + 1}`
    },
    resize () {
      const vw = typeof document !== 'undefined' ? document.documentElement.clientWidth : 1920
      this.documentViewportWidth = vw
      let k = vw / 1920
      if (this.isPanelCompactLayout) {
        const scroll = this.$refs.topicScrollEl
        const avail = scroll && scroll.clientWidth ? scroll.clientWidth : 420
        /* 与首页 AboutBloom 一致：整树设计宽 553@1920，统一缩放保证连线对齐 */
        k = Math.max(avail / 553, 0.55)
      }
      this.layoutScale = k
      this.aiNameBigWidth = 96 * k
      this.aiNameWidth = 104 * k
      this.aiNameTreeWidth = 43 * k
      this.aiSecondWidth = 70 * k
      this.updateTopicFitToChat()
    },
    ensureTopicBoxFitObserver () {
      const el = this.$refs.topicScrollEl
      if (!el || typeof ResizeObserver === 'undefined') return
      if (this._topicBoxRoObserved === el) return
      if (this._topicBoxRo) {
        this._topicBoxRo.disconnect()
        this._topicBoxRo = null
      }
      this._topicBoxRoObserved = el
      this._topicBoxRo = new ResizeObserver(() => this.resize())
      this._topicBoxRo.observe(el)
    },
    teardownTopicBoxFitObserver () {
      if (this._topicBoxRo) {
        this._topicBoxRo.disconnect()
        this._topicBoxRo = null
      }
      this._topicBoxRoObserved = null
    },
    /**
     * topicBox 设计宽度按 100vw 换算，在窄聊天列会超出；按滚动容器 clientWidth 等比缩小，使视觉最大宽度不超过聊天框
     */
    updateTopicFitToChat () {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.applyTopicFitMeasure()
          /* 竖排或缩放后高度变化，再测一次 shim */
          if (this.topicNeedsScale || this.isTopicLayoutCompact) {
            requestAnimationFrame(() => {
              this.applyTopicFitMeasure()
            })
          }
        })
      })
    },
    /**
     * 与样式一致的设计宽度：1669 * (100vw/1920)，不依赖当前横/竖排 offsetWidth，避免两种模式都误判为竖排
     */
    topicBoxDesignWidthPx () {
      const vz = typeof document !== 'undefined' ? document.documentElement.clientWidth : 1920
      return (1669 * vz) / 1920
    },
    applyTopicFitMeasure () {
      const scroll = this.$refs.topicScrollEl
      const box = this.$refs.topicBoxEl
      if (!scroll || !box) {
        this.topicLayoutCompact = false
        this.topicNeedsScale = false
        this.topicBoxScale = 1
        this.topicShimHeight = 0
        return
      }
      const avail = scroll.clientWidth
      const designW = this.topicBoxDesignWidthPx()
      if (!avail || designW <= 0) {
        this.topicLayoutCompact = false
        this.topicNeedsScale = false
        this.topicBoxScale = 1
        this.topicShimHeight = 0
        return
      }
      /*
       * 竖排阈值：仅当聊天列明显偏窄时用 topicBox--compact（三列上下叠）。
       * 若与「整屏设计宽」绑在一起，伴学侧栏几乎永远 < designW，会永远竖排。
       */
      const verticalBreakpoint = Math.min(900, Math.max(340, designW * 0.42))
      this.topicLayoutCompact = this.isPanelCompactLayout || avail + 2 < verticalBreakpoint

      /* 缩小/窄视口竖排：仅用 layoutScale + CSS var(--topic-k) 统一缩放，不用 transform scale */
      if (this.isPanelCompactLayout) {
        this.topicNeedsScale = false
        this.topicBoxScale = 1
        this.topicShimHeight = 0
        return
      }

      /*
       * 缩放：设计宽（整屏 vw）大于列宽，或实际布局已比列宽更宽时启用。
       */
      const wBox = box.offsetWidth
      this.topicNeedsScale = avail + 0.5 < designW || wBox > avail + 2
      if (!this.topicNeedsScale) {
        this.topicBoxScale = 1
        this.topicShimHeight = 0
        return
      }
      const h = box.offsetHeight
      const hPad = 8
      const availSafe = Math.max(avail - hPad, 1)
      const refW = Math.max(designW, wBox, 1)
      let s = Math.min(1, availSafe / refW)
      /* 缩小竖排：树总高超过视口可用高度时再缩小，避免「未超高也被压扁」 */
      if (this.isTopicLayoutCompact && h > 0 && typeof window !== 'undefined') {
        const maxH = Math.min(window.innerHeight * 0.68, 660)
        const vPad = 20
        if (h > maxH - vPad) {
          s = Math.min(s, (maxH - vPad) / h)
        }
      }
      this.topicBoxScale = s
      this.topicShimHeight = h > 0 ? h * s : 0
    },
    scheduleMeasure () {
      setTimeout(() => this.getKeywordWidth(), 400)
    },
    getKeywordWidth () {
      if (!this.normalizedTopicTree.length) return
      this.$nextTick(() => {
        setTimeout(() => {
          const uid = this.instanceUid
          const nextWidths = []
          for (let i = 1; i <= 18; i++) {
            const el = document.getElementById(`aas-tdit-${uid}-${i}`)
            nextWidths[i - 1] = el ? el.offsetWidth + 15 : 0
          }
          this.aiNameColorWidth = nextWidths
          const nextHeights = []
          for (let i = 1; i <= 9; i++) {
            const el = document.getElementById(`aas-tdl-${uid}-${i}`)
            nextHeights[i - 1] = el ? el.offsetHeight * 2 : 0
          }
          this.aiSecondHeight = nextHeights
          this.resize()
        }, 200)
      })
    },
    syncRelationIds () {
      const list = this.topicList || []
      if (!list.length) {
        this.topicRelationNodeIds = []
        return
      }
      const idx = Math.min(Math.max(0, this.showAiTopic), list.length - 1)
      const row = list[idx]
      const rel = row && row.topicRelationNodeIds ? row.topicRelationNodeIds : []
      const topicRelationNodeIds = []
      rel.forEach(item => {
        String(item)
          .split(',')
          .forEach(id => {
            const t = id.trim()
            if (t) topicRelationNodeIds.push(t)
          })
      })
      this.topicRelationNodeIds = topicRelationNodeIds
    },
    clickTopicTitle () {
      const n = (this.topicList || []).length
      if (n <= 1) return
      this.showAiTopic = this.showAiTopic === n - 1 ? 0 : this.showAiTopic + 1
      this.syncRelationIds()
    }
  }
}
</script>

<style scoped lang="scss">
$width: 100vw/1920;

.ai-assistant-topic-fence {
  width: 100%;
  max-width: 100%;
  margin: 0 auto 16 * $width;
  box-sizing: border-box;
}

.ai-assistant-topic-fence__scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* 等比缩放后宽度已适配列宽，不再横向滚动，避免半截图 */
.ai-assistant-topic-fence__scroll--scaled {
  overflow-x: hidden;
}

/*
 * 缩小竖排：限制选题区最大高度，getBoundingClientRect 才有意义，scale 可同时压宽高，完整落在可视内；
 * 若仍略超高，仅在本容器内纵向滚动。
 */
.ai-assistant-topic-fence__scroll--compact {
  /* 原生 CSS min()，避免 Sass 把 min() 当编译期函数导致 vh 与 px 单位冲突 */
  max-height: unquote('min(72vh, 640px)');
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.ai-assistant-topic-fence__scale-inner {
  display: inline-block;
  vertical-align: top;
  /* 与父级同宽，避免 inline-block 在部分布局下比列宽多出一块 */
  max-width: 100%;
  box-sizing: border-box;
}

.topicTextBox {
  margin-top: 24 * $width;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  .topicTitleBox {
    width: 100%;
    max-width: 792 * $width;
    margin: 0 auto;
    padding: 2 * $width;
    height: fit-content;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 20 * $width;
    box-shadow: 3 * $width 4 * $width 20 * $width 4 * $width #dee8ff;
    background: linear-gradient(90deg, rgba(41, 107, 255, 1), rgba(106, 41, 255, 1));
    box-sizing: border-box;

    .topicTitleBoxItem {
      width: 100%;
      max-width: 790 * $width;
      height: fit-content;
      border-radius: 18 * $width;
      background: #ffffff;
      padding: 14 * $width 24 * $width;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      box-sizing: border-box;

      .topicTitle {
        flex: 1;
        min-width: 0;
        font-family: PingFangSC, PingFang SC;
        font-weight: 500;
        font-size: 20px;
        color: #030a17;
        line-height: 28px;
        word-break: break-word;
      }

      .topicTitleImg {
        width: 24 * $width;
        height: 22 * $width;
        margin-top: 7 * $width;
        cursor: pointer;
        flex-shrink: 0;
      }
    }
  }

  .topicDescTxt {
    padding-top: 20 * $width;
    width: 100%;
    max-width: 796 * $width;
    margin: 0 auto;
    font-family: PingFangSC, PingFang SC;
    font-weight: 400;
    font-size: 16px;
    color: #9499a4;
    line-height: 24px;
    text-align: left;
    font-style: normal;
    box-sizing: border-box;
    word-break: break-word;
  }
}

/* 放大：flex row；缩小：.topicBox--compact 覆盖为 column */
.topicBox {
  width: 1669 * $width;
  min-height: 420 * $width;
  height: auto;
  background: url('https://img.webloom.cn/img/aiChooseBg.png') no-repeat center center;
  background-size: 100% 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-around;
  font-family: MyFontAiTitle, MyFontAiTitle;
  text-align: left;
  font-style: normal;
  font-weight: bold;
  box-sizing: border-box;

  .topicItem {
    flex: 0 0 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    .topicName {
      position: relative;
      width: 137 * $width;
      height: 80 * $width;

      .topicNameImg {
        width: 137 * $width;
        height: 80 * $width;
      }

      .topicNameTxt {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 80 * $width;
        word-break: break-word;
        text-align: center;
        font-size: 15px;
        color: #ffffff;
        line-height: 18px;
      }
    }

    .topicDescBox {
      height: 333 * $width;
      display: flex;
      flex-flow: column;
      position: relative;
      left: -12 * $width;

      .topicDesc {
        height: 83 * $width;
        display: flex;
        align-items: center;

        .topicDescTree {
          margin-top: 20 * $width;
          margin-left: 7 * $width;
        }

        .topicDescItemBox {
          display: flex;
          flex-flow: column;
          position: relative;

          .topicDescItemB {
            padding-left: 4 * $width;
            padding-top: 34 * $width;
            height: 24 * $width;

            .topicDescItemTxt {
              width: fit-content;
              font-size: 11px;
              line-height: 16px;
            }

            .topicDescItemColor {
              position: relative;
              top: -6 * $width;
            }

            .topicDescItemBorder {
              position: absolute;
              bottom: 4 * $width;
              left: 50%;
              transform: translateX(-58%);
            }
          }

          .topicDescItemB1 {
            padding-top: 0;
          }
        }

        .topicDescItem {
          margin-top: 26 * $width;
          position: relative;
          width: 104 * $width;
          height: 47 * $width;

          .topicDescBorder {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }

          .topicDescLabel {
            width: 70 * $width;
            text-align: center;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            font-size: 12px;
            color: #ffffff;
            line-height: 14px;
          }
        }
      }

      .topicDesc2 {
        margin-top: 28 * $width;
      }

      .topicDesc3 {
        margin-top: 28 * $width;
      }
    }
  }

  /* 缩小模式：只改主轴为纵向；尺寸与 topicDescBox 偏移与默认一致，整块由外层 scale 压入可视区 */
  &.topicBox--compact {
    width: 1669 * $width;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 36 * $width;
    min-height: auto;
    padding: 24 * $width 12 * $width 32 * $width;
    box-sizing: border-box;

    .topicItem {
      flex: 0 0 auto;
      align-self: center;
    }
  }

  /* 伴学助手缩小面板：单列居中，布局尺寸随 --topic-k，字号用固定 px 下限 */
  &.topicBox--compact.topicBox--panel-compact {
    --topic-k: 1;
    width: 100%;
    max-width: 100%;
    min-height: auto;
    margin-left: auto;
    margin-right: auto;
    gap: 24px;
    padding: 16px 8px 24px;
    background-size: 100% auto;
    align-items: center;

    .topicItem {
      width: max-content;
      max-width: 100%;
      margin-left: auto;
      margin-right: auto;
      align-self: center;
      justify-content: center;
    }

    .topicName {
      width: calc(137px * var(--topic-k));
      height: calc(80px * var(--topic-k));
      flex-shrink: 0;

      .topicNameImg {
        width: calc(137px * var(--topic-k));
        height: calc(80px * var(--topic-k));
      }

      .topicNameTxt {
        width: calc(96px * var(--topic-k));
        max-width: 94%;
        font-size: 14px;
        line-height: 18px;
      }
    }

    .topicDescBox {
      height: calc(333px * var(--topic-k));
      min-height: calc(333px * var(--topic-k));
      left: calc(-12px * var(--topic-k));
      flex-shrink: 0;

      .topicDesc {
        height: calc(83px * var(--topic-k));
        min-height: calc(83px * var(--topic-k));
        flex-shrink: 0;
        align-items: center;

        .topicDescTree {
          margin-top: calc(20px * var(--topic-k));
          margin-left: calc(7px * var(--topic-k));
          flex-shrink: 0;
        }

        .topicDescItem {
          margin-top: calc(26px * var(--topic-k));
          width: calc(104px * var(--topic-k));
          min-width: calc(104px * var(--topic-k));
          height: calc(47px * var(--topic-k));
          flex-shrink: 0;

          .topicDescLabel {
            width: calc(70px * var(--topic-k));
            min-width: calc(48px * var(--topic-k));
            max-width: 92%;
            font-size: 12px;
            line-height: 14px;
            word-break: keep-all;
            overflow-wrap: normal;
            white-space: normal;
          }
        }

        .topicDescItemBox {
          display: flex;
          flex-flow: column;
          position: relative;
          flex: none;

          .topicDescItemB {
            padding-left: calc(4px * var(--topic-k));
            padding-top: calc(34px * var(--topic-k));
            height: calc(24px * var(--topic-k));
            min-height: calc(24px * var(--topic-k));

            .topicDescItemTxt {
              width: fit-content;
              font-size: 11px;
              line-height: 16px;
              word-break: keep-all;
              white-space: normal;
            }

            .topicDescItemColor {
              top: calc(-6px * var(--topic-k));
            }

            .topicDescItemBorder {
              bottom: calc(4px * var(--topic-k));
            }
          }

          .topicDescItemB1 {
            padding-top: 0;
          }
        }
      }

      .topicDesc2 {
        margin-top: calc(28px * var(--topic-k));
      }

      .topicDesc3 {
        margin-top: calc(28px * var(--topic-k));
      }
    }
  }
}

/* 伴学助手缩小模式：选题区居中，字号固定 px */
.ai-assistant-topic-fence--panel-compact {
  width: 100%;

  .ai-assistant-topic-fence__scroll {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-height: none;
    overflow-x: hidden;
    overflow-y: visible;
  }

  .ai-assistant-topic-fence__scale-shim--panel-compact {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .ai-assistant-topic-fence__scale-inner--panel-compact {
    display: block;
    width: 100%;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .topicTextBox {
    width: 100%;
    margin-top: 16px;

    .topicTitleBox {
      max-width: 100%;
    }

    .topicTitleBoxItem {
      max-width: 100%;
    }

    .topicDescTxt {
      max-width: 100%;
      padding-top: 12px;
    }
  }

  .topicTitle {
    font-size: 16px;
    line-height: 22px;
  }

  .topicDescTxt {
    font-size: 14px;
    line-height: 22px;
  }

  .topicTitleImg {
    width: 24px;
    height: 22px;
    min-width: 24px;
    min-height: 22px;
  }

  /* 覆盖基础 $width 规则，保持与 SVG 连线一致的几何关系 */
  .topicBox.topicBox--panel-compact .topicDescBox {
    height: calc(333px * var(--topic-k, 1));
    left: calc(-12px * var(--topic-k, 1));
  }

  .topicBox.topicBox--panel-compact .topicDesc {
    height: calc(83px * var(--topic-k, 1));
  }

  .topicBox.topicBox--panel-compact .topicDescItem {
    width: calc(104px * var(--topic-k, 1));
    height: calc(47px * var(--topic-k, 1));
    margin-top: calc(26px * var(--topic-k, 1));
  }

  .topicBox.topicBox--panel-compact .topicDescItem .topicDescLabel {
    width: calc(70px * var(--topic-k, 1));
    word-break: keep-all;
    white-space: normal;
  }

  .topicBox.topicBox--panel-compact .topicDescTree {
    margin-top: calc(20px * var(--topic-k, 1));
    margin-left: calc(7px * var(--topic-k, 1));
  }

  .topicBox.topicBox--panel-compact .topicDescItemB {
    height: calc(24px * var(--topic-k, 1));
    padding-top: calc(34px * var(--topic-k, 1));
  }

  .topicBox.topicBox--panel-compact .topicDesc2 {
    margin-top: calc(28px * var(--topic-k, 1));
  }

  .topicBox.topicBox--panel-compact .topicDesc3 {
    margin-top: calc(28px * var(--topic-k, 1));
  }
}
</style>



// WEBPACK FOOTER //
// src/components/public/ai-assistant-topic-fence.vue